import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

// localStorageに保存する「閉じた日時」のキー。値はISO形式の日時文字列。
const DISMISSED_AT_STORAGE_KEY = 'rakuneko.migrationBanner.dismissedAt';

// 閉じてから再表示するまでの日数
const REAPPEAR_AFTER_DAYS = 14;
const REAPPEAR_AFTER_MS = REAPPEAR_AFTER_DAYS * 24 * 60 * 60 * 1000;

const WEEKDAY_LABELS_JA = ['日', '月', '火', '水', '木', '金', '土'];
const WEEKDAY_LABELS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS_EN = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

@Component({
    selector: 'app-migration-banner',
    templateUrl: './migration-banner.component.html',
    styleUrls: ['./migration-banner.component.scss']
})
export class MigrationBannerComponent implements OnInit, OnDestroy {
    readonly meritKeys: string[] = [
        'migrationBanner.merits.item1',
        'migrationBanner.merits.item2',
        'migrationBanner.merits.item3',
        'migrationBanner.merits.item4',
        'migrationBanner.merits.item5'
    ];

    visible = false;
    meritsExpanded = false;
    message1Html: SafeHtml;

    private langChangeSubscription: Subscription;

    constructor(
        private readonly translateService: TranslateService,
        private readonly sanitizer: DomSanitizer
    ) { }

    ngOnInit() {
        this.visible = this.shouldShow();
        this.updateMessage1();
        this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
            this.updateMessage1();
        });
    }

    ngOnDestroy() {
        if (this.langChangeSubscription) {
            this.langChangeSubscription.unsubscribe();
        }
    }

    toggleMerits() {
        this.meritsExpanded = !this.meritsExpanded;
    }

    openNewManagementConsole() {
        this.redirectTo(environment.newManagementConsoleUrl);
    }

    dismiss() {
        this.visible = false;
        try {
            localStorage.setItem(DISMISSED_AT_STORAGE_KEY, new Date().toISOString());
        } catch (error) {
            // プライベートモード等でlocalStorageが使用できない場合は保存を諦める
        }
    }

    // window.location遷移をテストしやすいようメソッドとして分離
    protected redirectTo(url: string) {
        window.location.href = url;
    }

    private shouldShow(): boolean {
        let dismissedAt: string;
        try {
            dismissedAt = localStorage.getItem(DISMISSED_AT_STORAGE_KEY);
        } catch (error) {
            // プライベートモード等でlocalStorageが使用できない場合は表示する
            return true;
        }

        if (!dismissedAt) {
            return true;
        }

        const dismissedTime = Date.parse(dismissedAt);
        if (isNaN(dismissedTime)) {
            // 不正な値が保存されていた場合も表示する
            return true;
        }

        return Date.now() - dismissedTime >= REAPPEAR_AFTER_MS;
    }

    // 翻訳ファイルの読み込み完了を待つため、instantではなくgetを使う
    private updateMessage1() {
        const lang = this.translateService.currentLang || this.translateService.getDefaultLang();
        const date = this.formatEndDate(lang);
        this.translateService.get('migrationBanner.message1', { date }).subscribe((raw: string) => {
            this.message1Html = this.sanitizer.bypassSecurityTrustHtml(raw);
        });
    }

    // ja localeがregisterLocaleDataで登録されていないため、formatDateではなく手動で整形する
    private formatEndDate(lang: string): string {
        const endDate = new Date(environment.oldManagementConsoleEndDate);
        if (isNaN(endDate.getTime())) {
            return environment.oldManagementConsoleEndDate;
        }

        // 日付のみのISO文字列はUTCとして解釈されるため、UTCのgetterで取得する
        const year = endDate.getUTCFullYear();
        const month = endDate.getUTCMonth();
        const day = endDate.getUTCDate();
        const weekday = endDate.getUTCDay();

        if (lang === 'en') {
            return `${MONTH_LABELS_EN[month]} ${day}, ${year} (${WEEKDAY_LABELS_EN[weekday]})`;
        }
        return `${year}年${month + 1}月${day}日（${WEEKDAY_LABELS_JA[weekday]}）`;
    }
}
